import React from "react";
import { Form as FinalForm, Field } from "react-final-form";
import * as Yup from "yup";
import * as palette from "@govlaunch/palette";
import { Filestack, Button } from "@govlaunch/core";
import ResourceFieldError from "~/components/form/resources/ResourceFieldError";
import TextareaAutosize from "react-textarea-autosize";
import { Anchor } from "@govlaunch/web";
import LinkIcon from "~/components/icons/LinkIcon";
import DocumentIcon from "~/components/icons/DocumentIcon";
import FontIcon from "~/components/icons/FontIcon";
import { Margin } from "~/components/spacings";
import validate from "~/lib/utils/validate";
import { prefixWithHttpIfNecessary } from "@govlaunch/web/src/utils";
import { FormattedMessage, useIntl } from "react-intl";
import useIntlValidationMessages from "~/lib/hooks/useIntlValidationMessages";
import { omit } from "lodash/fp";

interface IResourceForm {
  onSubmit: (values: any) => any;
  onClose: () => any;
  allowLinks?: boolean;
  allowDocuments?: boolean;
  allowText?: boolean;
}

export default function ResourceForm({
  onSubmit,
  onClose,
  allowLinks = true,
  allowDocuments = true,
  allowText = false,
}: IResourceForm) {
  const intl = useIntl();
  const intlValidationMessages = useIntlValidationMessages();

  return (
    <FinalForm
      keepDirtyOnReinitialize={true}
      initialValues={{
        type: "link",
      }}
      onSubmit={onSubmit}
      validate={(values) => validateResourceForm(values, intlValidationMessages)}
    >
      {({ handleSubmit, values }) => (
        <form onSubmit={handleSubmit}>
          <div
            css={{
              width: 350,
              padding: 15,
              marginTop: 10,
            }}
          >
            <Field name="type">
              {({ input }) => (
                <div
                  css={{
                    display: "flex",
                    justifyContent: "space-around",
                    width: 100,
                    margin: "0 auto",
                  }}
                >
                  {allowLinks && (
                    <div
                      css={{
                        position: "relative",
                        display: "flex",
                        cursor: "pointer",
                      }}
                      onClick={() => input.onChange("link")}
                    >
                      <LinkIcon width={24} height={24} fill={palette.primary} />
                      {values.type === "link" && (
                        <div
                          css={{
                            height: 2,
                            width: "100%",
                            position: "absolute",
                            bottom: -4,
                            left: 0,
                            background: palette.primary,
                          }}
                        />
                      )}
                    </div>
                  )}

                  {allowDocuments && (
                    <div
                      css={{
                        position: "relative",
                        display: "flex",
                        cursor: "pointer",
                      }}
                      onClick={() => input.onChange("download")}
                    >
                      <DocumentIcon width={24} height={24} fill={palette.primary} />
                      {values.type === "download" && (
                        <div
                          css={{
                            height: 2,
                            width: "100%",
                            position: "absolute",
                            bottom: -4,
                            left: 0,
                            background: palette.primary,
                          }}
                        />
                      )}
                    </div>
                  )}

                  {allowText && (
                    <div
                      css={{
                        position: "relative",
                        display: "flex",
                        cursor: "pointer",
                      }}
                      onClick={() => input.onChange("text")}
                    >
                      <FontIcon width={24} height={24} fill={palette.primary} />
                      {values.type === "text" && (
                        <div
                          css={{
                            height: 2,
                            width: "100%",
                            position: "absolute",
                            bottom: -4,
                            left: 0,
                            background: palette.primary,
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </Field>

            <div
              css={{
                paddingTop: 15,
                paddingBottom: 15,
              }}
            >
              <Field name="title">
                {({ input, meta }) => (
                  <>
                    <input
                      css={{
                        height: 42,
                        borderRadius: 5,
                        border: `1px solid ${palette.lightestGray}`,
                        fontSize: 14,
                        padding: "5px 10px",
                        width: "100%",
                        outline: 0,
                        "::placeholder": {
                          color: palette.sealBlue,
                        },
                        ":focus": {
                          borderColor: palette.primary,
                        },
                      }}
                      placeholder={intl.formatMessage({
                        defaultMessage: "Title",
                        id: "9a9+ww",
                      })}
                      {...input}
                    />
                    <ResourceFieldError meta={meta} />
                  </>
                )}
              </Field>

              <Margin mt={10}>
                {values.type === "download" && (
                  <Field name="url">
                    {({ input, meta }) => (
                      <>
                        <Filestack
                          onSuccess={({ filesUploaded }: any) => input.onChange(filesUploaded[0].url)}
                          customRender={({ onPick }: { onPick: any }) => (
                            <Button color={palette.primary} textColor="#fff" size="xsmall" onClick={onPick}>
                              <FormattedMessage defaultMessage="Upload File" id="ryDJAn" />
                            </Button>
                          )}
                        />
                        {input.value && (
                          <Margin mt={5}>
                            <Anchor
                              css={{
                                fontSize: 12,
                                color: palette.primary,
                              }}
                              href={input.value}
                              external="outside-govlaunch"
                            >
                              {input.value}
                            </Anchor>
                          </Margin>
                        )}

                        <ResourceFieldError meta={meta} />
                      </>
                    )}
                  </Field>
                )}

                {values.type === "link" && (
                  <Field name="url">
                    {({ input, meta }) => (
                      <>
                        <input
                          css={{
                            height: 42,
                            borderRadius: 5,
                            border: `1px solid ${palette.lightestGray}`,
                            fontSize: 14,
                            padding: "5px 10px",
                            width: "100%",
                            outline: 0,
                            "::placeholder": {
                              color: palette.sealBlue,
                            },
                            ":focus": {
                              borderColor: palette.primary,
                            },
                          }}
                          type="url"
                          placeholder="https://"
                          {...input}
                          onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                            let url = event.target.value.trim();
                            url = prefixWithHttpIfNecessary(url, true) || "";
                            input.onChange(url);
                          }}
                        />
                        <ResourceFieldError meta={meta} />
                      </>
                    )}
                  </Field>
                )}

                {values.type === "text" && (
                  <Field name="body">
                    {({ input, meta }) => (
                      <>
                        <TextareaAutosize
                          {...omit(["onFocus"], input)}
                          minRows={2}
                          css={{
                            resize: "none",
                            width: "100%",
                            outline: 0,
                            borderRadius: 5,
                            border: `1px solid ${palette.lightestGray}`,
                            padding: "5px 10px",
                            fontSize: 16,
                            "::placeholder": {
                              color: palette.sealBlue,
                            },
                            ":focus": {
                              borderColor: palette.primary,
                            },
                          }}
                          placeholder="Enter text"
                        />
                        <ResourceFieldError meta={meta} />
                      </>
                    )}
                  </Field>
                )}
              </Margin>
            </div>

            <div
              css={{
                display: "flex",
              }}
            >
              <Button color={palette.primary} type="submit" textColor="#fff">
                <FormattedMessage defaultMessage="Save" id="jvo0vs" />
              </Button>
              <div
                css={{
                  width: 15,
                }}
              />
              <Button color={palette.primary} theme="secondary" onClick={onClose}>
                <FormattedMessage defaultMessage="Cancel" id="47FYwb" />
              </Button>
            </div>
          </div>
        </form>
      )}
    </FinalForm>
  );
}

function validateResourceForm(
  values: Record<string, any>,
  validationMessages: ReturnType<typeof useIntlValidationMessages>,
) {
  if (values.type === "download") {
    return validate(
      Yup.object().shape({
        title: Yup.string().required(validationMessages.cantBeBlank),
        url: Yup.string().url(validationMessages.invalidUrl).required(validationMessages.cantBeBlank),
      }),
    )(values);
  }

  if (values.type === "link") {
    return validate(
      Yup.object().shape({
        title: Yup.string().required(validationMessages.cantBeBlank),
        url: Yup.string().url(validationMessages.invalidUrl).required(validationMessages.cantBeBlank),
      }),
    )(values);
  }

  return validate(
    Yup.object().shape({
      title: Yup.string().required(validationMessages.cantBeBlank),
      body: Yup.string().required(validationMessages.cantBeBlank),
    }),
  )(values);
}
